import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import DocumentStore from 'notebook/documents/DocumentStore';
import {CrossStepFormulaBox} from 'notebook/tables/core-ui/CrossStepFormulaBox';
import spreadsheetStore, {SpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {createShallowSelector} from 'notebook/tables/store/selectors/SelectorCreators';
import {ReorderStepEvent} from 'procedures/design/method-config/MethodEditor';
import EmptyStepConfigSection from 'procedures/design/step-config/EmptyStepConfigSection';
import {
  ProcedureStepConfigEditorContext,
  ProcedureStepConfigEditorContextValue,
} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {ADDING_STEP_TEXT, STEP_NAME_PLACEHOLDER} from 'procedures/design/step-config/StepConfigConstants';
import {
  addStepDirectlyToStepGroup,
  findStep,
  findStepWithGroup,
} from 'procedures/design/step-config/stepManagementUtils';
import StepSelectorSidebar from 'procedures/design/step-config/StepSelectorSidebar';
import useStepNameDraftStateEditor from 'procedures/design/step-config/useStepNameDraftStateEditor';
import {Method, ProcedureStepGroup, ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import {STEP_SELECTOR_SIDEBAR_WIDTH} from 'procedures/shared-components/step-config/step-nav/StepNavigationDesignConstants';
import {ellipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import TextInput from 'ui-library/inputs/TextInput';
import {CenteredSpinner} from 'ui-library/Spinner';
import {InputChangeEvent} from 'util/Types';

import {MAX_STEP_NAME_LENGTH} from '../ProcedureDesignConstants';

export type StepGroupUpdater = (originalStepGroups: Array<ProcedureStepGroup>) => Array<ProcedureStepGroup>;

interface StepConfigEditorProps {
  method: Method;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
  onReorderedStep: (params: ReorderStepEvent) => void;
  selectedStepCid: string | null;
  onChangeSelectedStep: (selectedStepCid: string) => void;
  isProcedureReadOnly: boolean;
}

/**
 * This queue is used only while a save is in flight, or what I call the "open window" period. The queue ensures that
 * changes made during this period are protected. Changes are then replayed when the window closes (i.e., the save completes).
 *
 * [Note 1]: This does not affect the responsiveness of the UI. The document UI is managed separately by
 * DocumentEditor and DocumentStore, and changes made during the "open window" period are rendered immediately.
 * All that we are queuing here are the messages to the procedure. So the procedure will be out of date, but
 * the user never sees that.
 *
 * [Note 2]: The reason for this queue is to prevent a race condition. When the window is "open" (i.e., a save is
 * in flight), the user can still make changes to the document. Every change results in a new `documentVersionUpdate`
 * being installed into the step. However, when the window closes (the in-flight autosave returns), the procedure
 * will null out those `documentVersionUpdate` values, since they act as one-time-use commands to the server.
 * (See syncPersistedDataIntoLocalFlowchart, which runs when the save API returns.)
 */
function useQueuedStepChanges(onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void): {
  setIsQueueOpenForBusiness: (isOpen: boolean) => void;
  pushQueuedChange: (stepGroupUpdater: StepGroupUpdater) => void;
  flushQueuedChanges: () => void;
} {
  const [_, _setIsQueueOpenForBusiness] = useState(false); // This state is only to trigger re-renders when needed
  const isQueueOpenRef = useRef(false);
  const queuedChangesRef = useRef<Array<StepGroupUpdater>>([]);

  const setIsQueueOpenForBusiness = useCallback((isOpen: boolean) => {
    _setIsQueueOpenForBusiness(isOpen);
    isQueueOpenRef.current = isOpen;
  }, []);

  const pushQueuedChange = useCallback(
    (stepGroupUpdater: StepGroupUpdater) => {
      if (isQueueOpenRef.current) {
        queuedChangesRef.current.push(stepGroupUpdater);
      } else {
        // Just do it immediately
        onChangeSteps(stepGroupUpdater);
      }
    },
    [onChangeSteps]
  );

  const flushQueuedChanges = useCallback((): void => {
    if (queuedChangesRef.current.length > 0) {
      const changes = queuedChangesRef.current;
      queuedChangesRef.current = [];

      // Chain updates to ensure each update operates on the result of the previous one rather than overwriting it
      onChangeSteps((stepGroups) => {
        let result = stepGroups;
        for (const change of changes) {
          result = change(result);
        }
        return result;
      });
    }
  }, [onChangeSteps]);

  return {setIsQueueOpenForBusiness, pushQueuedChange, flushQueuedChanges};
}

export default function StepConfigEditor(props: StepConfigEditorProps): JSX.Element {
  const {saveImmediately, isSaving} = ProcedurePageContext.useContext();
  const {onChangeSteps: _onChangeSteps, onChangeSelectedStep, selectedStepCid, method} = props;
  // Create a ref to onChangeSteps so that long-lived coroutines can resolve to the latest
  // version. This shouldn't be used directly; all changes should use `pushQueuedChange`
  const _onChangeStepsRef = useRef<(stepGroupUpdater: StepGroupUpdater) => void>(_onChangeSteps);
  const {setIsQueueOpenForBusiness, pushQueuedChange, flushQueuedChanges} = useQueuedStepChanges(
    _onChangeStepsRef.current
  );
  const {
    cid,
    stepGroups,
    dataParameterSettings,
    materialInputSettings,
    materialOutputSettings,
    equipmentSettings,
    parameterUsages,
  } = method;

  const tableNamespace = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS ? `ns-recipe-${cid}` : undefined;
  const crossStepFormulaBoxSelector = useMemo(
    () =>
      createShallowSelector.withTypes<SpreadsheetStoreState>()([(storeState) => storeState], (storeState) => {
        if (storeState.editingTable && storeState.fileId && tableNamespace && storeState.index) {
          return {
            stepId: storeState.fileId,
            tableName: storeState.editingTable,
            index: storeState.index,
            tableNamespace,
          };
        }
        return null;
      }),
    [tableNamespace]
  );

  const crossStepFormulaBox = useStoreSelector(spreadsheetStore, (store) =>
    tableNamespace ? crossStepFormulaBoxSelector(store.getState(tableNamespace)) : null
  );
  const editingStep = useMemo(() => {
    if (!crossStepFormulaBox) {
      return undefined;
    }
    for (const stepGroup of stepGroups) {
      for (const step of stepGroup.steps) {
        if (step.documentId === crossStepFormulaBox.stepId) {
          return step;
        }
      }
    }
    return undefined;
  }, [crossStepFormulaBox, stepGroups]);

  const onReturnToStep = useCallback(() => {
    if (!crossStepFormulaBox) {
      return;
    }
    for (const stepGroup of stepGroups) {
      for (const step of stepGroup.steps) {
        if (step.documentId === crossStepFormulaBox.stepId) {
          onChangeSelectedStep(step.cid);
          return;
        }
      }
    }
  }, [crossStepFormulaBox, stepGroups, onChangeSelectedStep]);

  const relatedDocumentIds = useMemo(
    () =>
      stepGroups.flatMap((group) =>
        group.steps.filter((step) => step.documentId != null).map((step) => step.documentId!)
      ),
    [stepGroups]
  );

  // each time we get a new onChangeSteps, update the ref
  useEffect(() => {
    _onChangeStepsRef.current = _onChangeSteps;
  }, [_onChangeSteps]);

  // When we finish saving, flush any queued changes
  useEffect(() => {
    setIsQueueOpenForBusiness(isSaving);
    if (!isSaving) {
      flushQueuedChanges();
    }
  }, [isSaving, flushQueuedChanges, setIsQueueOpenForBusiness]);

  const {step: selectedStep, group: currentStepGroup} = useMemo(
    () =>
      selectedStepCid == null || stepGroups == null
        ? {step: null, group: null}
        : findStepWithGroup(stepGroups, selectedStepCid),
    [stepGroups, selectedStepCid]
  );

  const draftStateInfo = useStepNameDraftStateEditor({
    method,
    currentlyEditingStepCid: selectedStepCid,
    onChangeSteps: pushQueuedChange,
  });
  const {draftName, setDraftName, onFocus, onBlur, handleEnterAndEscape} = draftStateInfo;

  const documentId = selectedStep?.documentId;
  const doesMethodHaveSteps = method.stepGroups.some((group) => group.steps.length > 0);

  const addEmptyPreRunStep = useCallback(async (): Promise<void> => {
    // When adding a new step, we need to trigger an immediate API call, which will create the
    // document to associate with this step
    await saveImmediately((latestFlowchart: ProcedureFlowchart) => {
      assert(
        latestFlowchart.nodes.every(
          (node) => node.cid !== cid || node.stepGroups.every((sg) => sg.steps.length === 0)
        ),
        'Expected all step groups to be empty for the selected method'
      );
      // Always add to first step group at first index
      pushQueuedChange((latestStepGroups) => {
        const {updatedStepGroups, newStepCid} = addStepDirectlyToStepGroup(latestStepGroups, 0, 0);
        onChangeSelectedStep(newStepCid);
        return updatedStepGroups;
      });
      return {
        blockingSaveObjectSource: BlockingSaveObjectSource.Step,
        blockingSaveOperation: BlockingSaveOperation.Create,
      };
    }, ADDING_STEP_TEXT);
  }, [cid, onChangeSelectedStep, saveImmediately, pushQueuedChange]);

  const documentSaveHandler = useCallback(async (): Promise<boolean> => {
    await saveImmediately(() => ({
      blockingSaveObjectSource: BlockingSaveObjectSource.Step,
      blockingSaveOperation: BlockingSaveOperation.Update,
    }));
    return true;
  }, [saveImmediately]);

  const handleDocumentOnChange = useCallback(() => {
    if (documentId != null && selectedStep != null && DocumentStore.hasUnsavedChanges(documentId)) {
      pushQueuedChange((stepGroups) =>
        produce(stepGroups, (draftStepGroups) => {
          const draftStep = findStep(draftStepGroups, selectedStep.cid);
          draftStep!.documentVersionUpdate = DocumentStore.getDocumentVersionUpdate(documentId);
        })
      );
    }
  }, [documentId, pushQueuedChange, selectedStep]);

  const procedureStepConfigEditorContextValue: ProcedureStepConfigEditorContextValue = useMemo(
    () => ({
      parameterSettings: {
        dataParameterSettings,
        materialInputSettings,
        materialOutputSettings,
        equipmentSettings,
      },
      parameterUsages,
      currentStepGroup,
    }),
    [
      dataParameterSettings,
      materialInputSettings,
      materialOutputSettings,
      equipmentSettings,
      parameterUsages,
      currentStepGroup,
    ]
  );

  return (
    <ProcedureStepConfigEditorContext.Provider value={procedureStepConfigEditorContextValue}>
      <div
        className={css(styles.editorPage)}
        {...getTestProps({component: 'StepConfigEditor', element: 'root'})}
      >
        <StepSelectorSidebar
          method={props.method}
          onChangeSteps={pushQueuedChange}
          selectedStepCid={selectedStepCid}
          onChangeSelection={onChangeSelectedStep}
          onReorderedStep={props.onReorderedStep}
          sidebarWidth={STEP_SELECTOR_SIDEBAR_WIDTH}
          locked={props.isProcedureReadOnly}
          draftStateEditorInfo={draftStateInfo}
        />
        <div className={classNames('u-flex-grow u-min-width-zero', css(styles.documentWrapper))}>
          {(() => {
            if (selectedStep == null) {
              if (doesMethodHaveSteps) {
                // Not expected to enter this state where steps are selected but none are present, but in case we do, just render an empty screen
                return <div />;
              } else {
                return (
                  <EmptyStepConfigSection
                    onClickAddStep={addEmptyPreRunStep}
                    allowAddStep={!props.isProcedureReadOnly}
                  />
                );
              }
            } else if (documentId != null) {
              return (
                <div className={css(styles.editorWithStepTitle)}>
                  <TextInput
                    value={draftName}
                    onChange={(e: InputChangeEvent) => {
                      setDraftName(e.target.value);
                    }}
                    className={css(styles.documentStepHeader)}
                    placeholder={STEP_NAME_PLACEHOLDER}
                    onKeyDown={handleEnterAndEscape}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={props.isProcedureReadOnly}
                    maxLength={MAX_STEP_NAME_LENGTH}
                    dataTestAttrs={{component: 'StepConfigEditor', element: 'stepTitleInput'}}
                  />
                  <DocumentEditor
                    id={documentId}
                    locked={props.isProcedureReadOnly}
                    disableInternalAutoSaver={true}
                    tableNamespace={tableNamespace}
                    onChange={handleDocumentOnChange}
                    customSaveHandler={documentSaveHandler}
                    relatedDocumentIds={
                      BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS ? relatedDocumentIds : undefined
                    }
                    disablePerformanceIndicator={true}
                    withDisplayOptions={true}
                  />
                  {BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS &&
                    crossStepFormulaBox &&
                    editingStep &&
                    selectedStep.documentId &&
                    crossStepFormulaBox.stepId !== selectedStep.documentId && (
                      <CrossStepFormulaBox
                        stepName={editingStep.name}
                        stepIdBeingEdited={crossStepFormulaBox.stepId}
                        stepIdBeingDisplayed={selectedStep.documentId}
                        tableName={crossStepFormulaBox.tableName}
                        index={crossStepFormulaBox.index}
                        tableNamespace={crossStepFormulaBox.tableNamespace}
                        onReturnToStep={onReturnToStep}
                      />
                    )}
                </div>
              );
            } else {
              return <CenteredSpinner />;
            }
          })()}
        </div>
      </div>
    </ProcedureStepConfigEditorContext.Provider>
  );
}

const styles = StyleSheet.create({
  documentWrapper: {
    overflowY: 'hidden',
  },
  editorWithStepTitle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  editorPage: {
    width: '100%',
    height: '100%',
    background: WHITE,
    display: 'flex',
  },
  documentStepHeader: {
    padding: '20px',
    paddingLeft: '13px',
    borderRadius: '0',
    border: `1px solid ${GRAY_LIGHT}`,
    borderLeftWidth: 0,
    fontSize: '18px',
    ...ellipsisText,
  },
});
